'use client'

import { useTranslations } from 'next-intl'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useStatsigGate } from '~/configs/statsig'
import {
  type AgreementResponse,
  type ProductResponse,
  useGetAgreementsQuery,
  useGetRequirementsQuery,
  useGetSettingsQuery,
  useLazyGetAgreementsQuery,
  useLazyListProductsQuery,
  useListProductsQuery,
  useListRegistrationsQuery,
  useListTaxRatesQuery
} from '~/data/api/client/billing'
import { useCurrentOrgId } from '~/store/slices/currentOrg'

import type { CompletenessCardItem } from './BillingCompletenessCard'
import { type BillingSetupCompleteness, computeBillingSetupCompleteness } from './completeness'

const PAGE_SIZE = 200

/**
 * Wires the org's products, agreements, tax configuration and metadata requirements into the client-side
 * setup checklist. Tax reads are skipped when `tax-management` is off, and the tax items drop out of the
 * score so a merchant without the tax hub is not told to visit it.
 */
export function useBillingSetupCompleteness(): {
  completeness: BillingSetupCompleteness
  isLoading: boolean
} {
  const orgId = useCurrentOrgId()
  const { enabled: taxEnabled, isLoading: taxGateLoading } = useStatsigGate('tax-management')
  const skip = !orgId
  const skipTax = skip || taxGateLoading || !taxEnabled
  const header = { 'X-Organization-Id': orgId ?? '' }

  const products = useAllProducts(skip, orgId)
  const agreements = useAllAgreements(skip, orgId)
  const requirements = useGetRequirementsQuery(header, { skip })
  const taxSettings = useGetSettingsQuery(header, { skip: skipTax })
  const taxRates = useListTaxRatesQuery({ ...header, pageSize: PAGE_SIZE }, { skip: skipTax })
  const registrations = useListRegistrationsQuery(header, { skip: skipTax })

  const completeness = useMemo(
    () =>
      computeBillingSetupCompleteness({
        products: products.items,
        agreements: agreements.items,
        requirements: requirements.data ?? null,
        ...(taxEnabled
          ? {
              taxSettings: taxSettings.data ?? null,
              taxRates: taxRates.data?.items ?? [],
              registrations: registrations.data ?? []
            }
          : {})
      }),
    [
      agreements.items,
      products.items,
      registrations.data,
      requirements.data,
      taxEnabled,
      taxRates.data,
      taxSettings.data
    ]
  )

  const isLoading =
    taxGateLoading ||
    products.isLoading ||
    agreements.isLoading ||
    requirements.isLoading ||
    (taxEnabled && (taxSettings.isLoading || taxRates.isLoading || registrations.isLoading))

  return { completeness, isLoading }
}

/** {@link useBillingSetupCompleteness} with the items resolved to localized card rows. */
export function useBillingSetupCardItems(): {
  score: number
  items: CompletenessCardItem[]
  isLoading: boolean
} {
  const t = useTranslations('features.BillingComposer')
  const { completeness, isLoading } = useBillingSetupCompleteness()
  const items = useMemo<CompletenessCardItem[]>(
    () =>
      completeness.items.map((item) => ({
        key: item.key,
        status: item.status,
        message: t(item.messageKey),
        href: item.href
      })),
    [completeness.items, t]
  )
  return { score: completeness.score, items, isLoading }
}

/**
 * Every product, not just the first page.
 *
 * Completeness flags missing pricing or assets only when it sees the incomplete product. A gap past
 * page 0 would otherwise look `done` and the hub checklist would report setup complete.
 */
function useAllProducts(skip: boolean, orgId: string | undefined): { items: ProductResponse[]; isLoading: boolean } {
  const firstPage = useListProductsQuery({ size: PAGE_SIZE, page: 0, organizationId: orgId }, { skip: skip || !orgId })
  const [fetchPage] = useLazyListProductsQuery()
  return useFollowingPages(skip || !orgId, firstPage, (page) =>
    fetchPage({ size: PAGE_SIZE, page, organizationId: orgId }).unwrap()
  )
}

/**
 * Every live agreement, not just the first page. Same reason as {@link useAllProducts}: empty
 * product assignments on a later page must still mark `agreements.products` as a warning.
 */
function useAllAgreements(skip: boolean, orgId: string | undefined): { items: AgreementResponse[]; isLoading: boolean } {
  const firstPage = useGetAgreementsQuery(
    { size: PAGE_SIZE, page: 0, includeArchived: false, expand: ['data.products'], payeeId: orgId },
    { skip: skip || !orgId }
  )
  const [fetchPage] = useLazyGetAgreementsQuery()
  return useFollowingPages(skip || !orgId, firstPage, (page) =>
    fetchPage({ size: PAGE_SIZE, page, includeArchived: false, expand: ['data.products'], payeeId: orgId }).unwrap()
  )
}

type PagedResult<T> = {
  data: T[]
  meta: { pagination: { totalPages: number } }
}

function useFollowingPages<T>(
  skip: boolean,
  firstPage: { data?: PagedResult<T>; isLoading: boolean },
  fetchPage: (page: number) => Promise<Pick<PagedResult<T>, 'data'>>
): { items: T[]; isLoading: boolean } {
  const fetchRef = useRef(fetchPage)
  fetchRef.current = fetchPage
  const [extra, setExtra] = useState<T[]>([])
  const [extraSettled, setExtraSettled] = useState(false)
  const totalPages = firstPage.data?.meta.pagination.totalPages ?? 1

  useEffect(() => {
    setExtra([])
    if (skip || !firstPage.data || totalPages <= 1) {
      setExtraSettled(true)
      return
    }

    let cancelled = false
    setExtraSettled(false)

    const load = async () => {
      const rest: T[] = []
      for (let page = 1; page < totalPages; page += 1) {
        if (cancelled) return
        const result = await fetchRef.current(page)
        rest.push(...result.data)
      }
      if (!cancelled) setExtra(rest)
    }

    void load()
      .catch(() => {
        // First page still stands; do not spin forever if a later page fails.
      })
      .finally(() => {
        if (!cancelled) setExtraSettled(true)
      })

    return () => {
      cancelled = true
    }
  }, [firstPage.data, skip, totalPages])

  const items = useMemo(() => [...(firstPage.data?.data ?? []), ...extra], [extra, firstPage.data?.data])

  return {
    items,
    isLoading: firstPage.isLoading || (totalPages > 1 && !extraSettled)
  }
}
